import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Send, X, Bot, ArrowRight, ListChecks } from 'lucide-react';
import { PUBLIC_CHIPS, runPublicQuery } from '../agent/publicKnowledge';
import { isPublicBackendEnabled, publicChatViaBackend } from '../agent/publicChatClient';
import appConfig from '../config/appConfig';

/**
 * PublicChatAssistant — the pre-login (sign-in page) helper.
 * =========================================================
 * A deliberately limited, PHI-free assistant that answers ONLY general
 * getting-started questions (create account, reset password/username, EFT/ERA
 * enrollment, contact). It cannot look up any member/claim/eligibility/payment
 * data — the backend endpoint it uses is tool-less, PHI-guarded, and rate-limited.
 *
 * Gated by the super-admin `loginAssistantEnabled` setting (OFF by default).
 * `ready` (from the login page's Turnstile) must be true before the user can
 * send — that keeps bots/crawlers from hammering it. Offline by default; uses
 * the backend only when VITE_AGENT_URL is set.
 */

const COLOR = { primary: '#0D9488', dark: '#0F766E', deep: '#134E4A' };

// Only user/assistant text turns, capped — sent to the backend.
const toHistory = (messages) =>
  messages
    .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.text && m.text.trim())
    .slice(-6)
    .map((m) => ({ role: m.role, content: m.text }));

export default function PublicChatAssistant({ ready = true }) {
  const navigate = useNavigate();
  const live = isPublicBackendEnabled();

  // Super-admin switch (default off). React to live config changes.
  const [enabled, setEnabled] = useState(() => appConfig.getLoginAssistantEnabled());
  useEffect(() => appConfig.subscribe(() => setEnabled(appConfig.getLoginAssistantEnabled())), []);

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef(null);

  const greeting = () => ({ role: 'assistant', text: "Hi, I'm Tarini — I can help you sign in or get started. Pick a topic:", chips: PUBLIC_CHIPS });
  const [messages, setMessages] = useState([greeting()]);
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const scrollToEnd = useCallback(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, []);
  useEffect(() => { if (open) scrollToEnd(); }, [messages, thinking, open, scrollToEnd]);

  const goto = useCallback((route) => { navigate(route); setOpen(false); }, [navigate]);

  const send = useCallback(async (raw) => {
    const q = (raw ?? '').trim();
    if (!q || thinking || !ready) return;
    setInput('');
    const userMsg = { role: 'user', text: q };
    const history = [...messagesRef.current, userMsg];
    setMessages((prev) => [...prev, userMsg]);

    // Offline public engine.
    if (!live) {
      setThinking(true);
      setTimeout(() => {
        const res = runPublicQuery(q);
        setThinking(false);
        setMessages((prev) => [...prev, { role: 'assistant', ...res }]);
      }, 300);
      return;
    }

    // Backend public endpoint — falls back to offline on any error.
    setThinking(true);
    try {
      const { text, cta, steps, stepsTitle } = await publicChatViaBackend({ messages: toHistory(history) });
      setThinking(false);
      setMessages((prev) => [...prev, { role: 'assistant', text: text || runPublicQuery(q).text, cta, steps, stepsTitle }]);
    } catch {
      setThinking(false);
      const res = runPublicQuery(q);
      setMessages((prev) => [...prev, { role: 'assistant', ...res }]);
    }
  }, [thinking, ready, live]);

  if (!enabled) return null;

  /* -- collapsed FAB ---------------------------------------------------- */
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        aria-label="Open Tarini portal help"
        title="Need help signing in? Ask Tarini"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9995, width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer', background: `linear-gradient(135deg, ${COLOR.primary}, ${COLOR.dark})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 28px rgba(13,148,136,0.42), 0 2px 6px rgba(0,0,0,0.14)' }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.04)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; }}
      >
        <MessageSquare size={24} strokeWidth={2.2} />
      </button>
    );
  }

  /* -- expanded panel --------------------------------------------------- */
  return (
    <div role="dialog" aria-label="Tarini portal help" style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9995, width: 384, maxWidth: 'calc(100vw - 32px)', height: 540, maxHeight: 'calc(100vh - 48px)', background: '#fff', borderRadius: 18, boxShadow: '0 20px 60px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.05)', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'pchatIn 0.22s cubic-bezier(.2,.8,.2,1)' }}>
      {/* header */}
      <div style={{ background: `linear-gradient(135deg, ${COLOR.dark}, ${COLOR.deep})`, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 11, flexShrink: 0 }}>
        <div style={{ width: 38, height: 38, borderRadius: 11, flexShrink: 0, background: 'rgba(255,255,255,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Bot size={20} color="#fff" strokeWidth={2.2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: '#fff' }}>Tarini</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)' }}>Central Health portal help · no sign-in needed</div>
        </div>
        <button onClick={() => setOpen(false)} aria-label="Close help" style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <X size={16} strokeWidth={2.5} />
        </button>
      </div>

      {/* messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#F8FAFB' }}>
        {messages.map((m, idx) => (
          <div key={idx} style={{ marginBottom: 14, display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{ maxWidth: '86%' }}>
              <div style={{ padding: '10px 12px', borderRadius: 14, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', ...(m.role === 'user' ? { background: `linear-gradient(135deg, ${COLOR.primary}, ${COLOR.dark})`, color: '#fff', borderBottomRightRadius: 5 } : { background: '#fff', color: '#334155', border: '1px solid #E8ECF0', borderBottomLeftRadius: 5 }) }}>
                {m.text}
              </div>
              {m.role === 'assistant' && (
                <>
                  {m.steps?.length > 0 && (
                    <div style={{ border: '1px solid #E8ECF0', borderRadius: 12, padding: 12, background: '#fff', marginTop: 8 }}>
                      {m.stepsTitle && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 12.5, fontWeight: 800, color: '#0F172A' }}>
                          <ListChecks size={15} strokeWidth={2.4} color={COLOR.primary} /> {m.stepsTitle}
                        </div>
                      )}
                      <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {m.steps.map((step, i) => (
                          <li key={i} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                            <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', background: `${COLOR.primary}14`, color: COLOR.dark, fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                            <span style={{ fontSize: 12, lineHeight: 1.5, color: '#334155' }}>{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                  {m.cta && (
                    <button onClick={() => goto(m.cta.route)} style={{ marginTop: 8, padding: '8px 14px', borderRadius: 9, border: 'none', background: `linear-gradient(135deg, ${COLOR.primary}, ${COLOR.dark})`, color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {m.cta.label} <ArrowRight size={14} strokeWidth={2.5} />
                    </button>
                  )}
                  {m.chips && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                      {m.chips.map((chip, i) => (
                        <button key={i} onClick={() => send(chip)} style={{ padding: '6px 11px', borderRadius: 20, cursor: 'pointer', border: `1px solid ${COLOR.primary}33`, background: '#fff', color: COLOR.dark, fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = `${COLOR.primary}0D`; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}>
                          {chip}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}

        {thinking && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 14 }}>
            <div style={{ padding: '12px 14px', borderRadius: 14, borderBottomLeftRadius: 5, background: '#fff', border: '1px solid #E8ECF0', display: 'flex', gap: 4 }}>
              {[0, 1, 2].map((i) => <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#94A3B8', animation: `pchatDot 1s ${i * 0.15}s infinite ease-in-out` }} />)}
            </div>
          </div>
        )}
      </div>

      {/* composer */}
      <div style={{ padding: 12, borderTop: '1px solid #E8ECF0', background: '#fff', flexShrink: 0 }}>
        {!ready && (
          <div style={{ fontSize: 10.5, color: '#94A3B8', textAlign: 'center', marginBottom: 8 }}>
            Verifying your browser… you can chat in a moment.
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
            placeholder="Ask about sign-in, EFT/ERA, forms…"
            aria-label="Message portal help"
            maxLength={1000}
            disabled={!ready}
            style={{ flex: 1, height: 42, borderRadius: 11, border: '1px solid #E2E8F0', padding: '0 14px', fontSize: 13, fontFamily: 'inherit', color: '#1E293B', outline: 'none', background: ready ? '#F8FAFB' : '#F1F5F9' }}
            onFocus={(e) => { e.currentTarget.style.borderColor = COLOR.primary; e.currentTarget.style.background = '#fff'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.background = ready ? '#F8FAFB' : '#F1F5F9'; }}
          />
          <button onClick={() => send(input)} disabled={!input.trim() || thinking || !ready} aria-label="Send" style={{ width: 42, height: 42, borderRadius: 11, border: 'none', flexShrink: 0, background: input.trim() && !thinking && ready ? `linear-gradient(135deg, ${COLOR.primary}, ${COLOR.dark})` : '#CBD5E1', color: '#fff', cursor: input.trim() && !thinking && ready ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Send size={17} strokeWidth={2.3} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pchatIn { 0% { opacity: 0; transform: translateY(12px) scale(0.98); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes pchatDot { 0%, 60%, 100% { transform: translateY(0); opacity: 0.5; } 30% { transform: translateY(-4px); opacity: 1; } }
      `}</style>
    </div>
  );
}
